import React from "react";
import { Row, Col } from "react-bootstrap";
import { Icon } from "@iconify/react";
import styles from './AccessosWiki.module.scss';

/**
 * 📚 Wiki - Seção Afiliados e Vendedores
 * 
 * Documentação completa sobre o sistema de afiliados, vendedores,
 * como criar, atribuir produtos, aprovar e editar comissões.
 * Voltada para produtores (não técnica).
 */
const AfiliadosWiki = () => {
  return (
    <div className={styles.wikiAccessos}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBadge}>
          <Icon icon="mdi:account-network" />
          <span>Afiliados e Vendedores</span>
        </div>
        <p className={styles.heroText}>
          Construa sua rede de vendas com afiliados e vendedores dedicados.
          Controle total sobre produtos, comissões e aprovações.
        </p>
      </section>

      {/* Tipos de Usuários de Vendas */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Tipos de Usuários de Vendas</span>
          <p className={styles.sectionSubtitle}>
            Escolha o tipo certo para cada parceiro de vendas
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.userCard}>
              <div className={`${styles.cardIcon} ${styles.iconSuccess}`}>
                <Icon icon="mdi:account-cash" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeSuccess}`}>Equipe Dedicada</div>
                <span className={styles.cardTitle}>Vendedor</span>
                <p>
                  Vendedores que trabalham <strong>exclusivamente para você</strong>.
                  São vinculados automaticamente a <strong>todos os seus produtos</strong>.
                </p>
                <ul className={styles.featureList}>
                  <li><Icon icon="mdi:check-circle" /> Acesso a TODOS os produtos automaticamente</li>
                  <li><Icon icon="mdi:check-circle" /> Novos produtos precisam ser aprovados manualmente</li>
                  <li><Icon icon="mdi:check-circle" /> Não pode acessar a loja pública</li>
                  <li><Icon icon="mdi:check-circle" /> Vê apenas suas próprias vendas e comissões</li>
                </ul>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.userCard}>
              <div className={`${styles.cardIcon} ${styles.iconWarning}`}>
                <Icon icon="mdi:account-network" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeWarning}`}>Convidado Direto</div>
                <span className={styles.cardTitle}>Afiliado Direto</span>
                <p>
                  Afiliados que <strong>você convida diretamente</strong>.
                  Você escolhe quais produtos eles podem promover.
                </p>
                <ul className={styles.featureList}>
                  <li><Icon icon="mdi:check-circle" /> Você seleciona os produtos permitidos</li>
                  <li><Icon icon="mdi:check-circle" /> Aprovado automaticamente (não precisa aprovar)</li>
                  <li><Icon icon="mdi:check-circle" /> Não pode acessar a loja pública</li>
                  <li><Icon icon="mdi:check-circle" /> Vê apenas suas próprias vendas e comissões</li>
                </ul>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.userCard}>
              <div className={`${styles.cardIcon}`}>
                <Icon icon="mdi:account-plus" />
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardBadge}>Auto-Cadastro</div>
                <span className={styles.cardTitle}>Afiliado Indireto</span>
                <p>
                  Afiliados que se cadastram <strong>por conta própria</strong> na loja pública
                  e solicitam afiliação aos seus produtos.
                </p>
                <ul className={styles.featureList}>
                  <li><Icon icon="mdi:check-circle" /> Se cadastra na loja pública</li>
                  <li><Icon icon="mdi:check-circle" /> Solicita afiliação aos produtos desejados</li>
                  <li><Icon icon="mdi:check-circle" /> Você precisa aprovar manualmente</li>
                  <li><Icon icon="mdi:check-circle" /> Pode acessar outros produtos na loja</li>
                </ul>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Como Criar */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Criar</span>
          <p className={styles.sectionSubtitle}>
            Passo a passo para cadastrar vendedores e afiliados diretos
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.stepCard}>
              <div className={styles.stepNumber}>1</div>
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>Acesse Usuários</span>
                <p>
                  Vá em <strong>Acessos → Usuários</strong> e clique no botão
                  <strong> "Novo Usuário"</strong>.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.stepCard}>
              <div className={styles.stepNumber}>2</div>
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>Escolha o Tipo</span>
                <p>
                  Selecione o tipo: <strong>Vendedor</strong> ou
                  <strong> Afiliado Direto</strong>.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.stepCard}>
              <div className={styles.stepNumber}>3</div>
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>Preencha os Dados</span>
                <p>
                  Informe: <strong>nome</strong>, <strong>email</strong>,
                  <strong> telefone</strong> e <strong>senha</strong>.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.stepCard}>
              <div className={styles.stepNumber}>4</div>
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>Vendedor: Produtos Depois</span>
                <p>
                  Se for <strong>Vendedor</strong>, ele será criado sem produtos vinculados.
                  Você pode adicionar produtos depois usando <strong>"Atribuir Afiliação"</strong> em "Meus Afiliados".
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.stepCard}>
              <div className={styles.stepNumber}>4</div>
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>Afiliado Direto: Selecione Produtos</span>
                <p>
                  Se for <strong>Afiliado Direto</strong>, selecione os produtos que ele pode vender agora.
                  Ele será aprovado automaticamente nesses produtos. Você pode adicionar mais depois.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.stepCard}>
              <div className={styles.stepNumber}>5</div>
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>Email de Acesso</span>
                <p>
                  O sistema enviará automaticamente um email com os dados de acesso
                  (email e senha) para o novo usuário.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Atribuir Produtos */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Atribuir Produtos</span>
          <p className={styles.sectionSubtitle}>
            Como adicionar novos produtos para vendedores e afiliados diretos
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:information" className={styles.infoIcon} />
          <div>
            <span className={styles.infoTitle}>Quando Usar "Atribuir Afiliação"</span>
            <p>
              Use esta funcionalidade quando quiser adicionar novos produtos para um
              vendedor ou afiliado direto que já foi cadastrado. O sistema mostra apenas
              os produtos que ainda não estão vinculados ao usuário.
            </p>
          </div>
        </div>

        <Row className="g-4 mt-3">
          <Col lg={4}>
            <div className={styles.stepCard}>
              <Icon icon="mdi:arrow-right-bold" className={styles.stepIcon} />
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>1. Acesse Meus Afiliados</span>
                <p>
                  Vá em <strong>Afiliados → Meus Afiliados</strong> e clique no botão
                  <strong> "Atribuir Afiliação"</strong>.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={4}>
            <div className={styles.stepCard}>
              <Icon icon="mdi:account-check" className={styles.stepIcon} />
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>2. Selecione o Usuário</span>
                <p>
                  Escolha um <strong>Vendedor</strong> ou <strong>Afiliado Direto</strong>
                  no dropdown. Apenas esses tipos aparecem na lista.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={4}>
            <div className={styles.stepCard}>
              <Icon icon="mdi:package-variant" className={styles.stepIcon} />
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>3. Selecione os Produtos</span>
                <p>
                  O sistema mostra apenas produtos <strong>não vinculados</strong>.
                  Selecione os produtos desejados e clique em
                  <strong> "Atribuir Produtos"</strong>.
                </p>
              </div>
            </div>
          </Col>
        </Row>

        <div className={styles.tipCard}>
          <Icon icon="mdi:lightbulb-on" className={styles.tipIcon} />
          <div>
            <span className={styles.tipTitle}>💡 Dica Importante</span>
            <p>
              Quando você cria um <strong>produto novo</strong>, ele <strong>NÃO é vinculado automaticamente</strong>
              a vendedores ou afiliados diretos. Você precisa usar <strong>"Atribuir Afiliação"</strong> para
              adicionar o produto aos vendedores/afiliados desejados.
            </p>
            <p className="mb-0">
              Isso dá mais controle sobre quais produtos cada pessoa pode promover.
            </p>
          </div>
        </div>
      </section>

      {/* Aprovar e Rejeitar */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Aprovar e Rejeitar Afiliados</span>
          <p className={styles.sectionSubtitle}>
            Como gerenciar solicitações de afiliação
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.stepCard}>
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>1. Localize Afiliados Pendentes</span>
                <p>
                  Vá em <strong>Afiliados → Meus Afiliados</strong> e use:
                </p>
                <ul className={styles.featureList}>
                  <li><Icon icon="mdi:filter" /> Filtro por tipo: "Indireto"</li>
                  <li><Icon icon="mdi:tab" /> Aba "Pendentes"</li>
                  <li><Icon icon="mdi:search" /> Busca por nome ou email</li>
                </ul>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.stepCard}>
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>2. Veja Detalhes do Afiliado</span>
                <p>
                  Clique no ícone <strong>"Ver detalhes"</strong> (ícone de olho) na tabela.
                  Um drawer lateral abrirá mostrando:
                </p>
                <ul className={styles.featureList}>
                  <li><Icon icon="mdi:account" /> Dados do afiliado</li>
                  <li><Icon icon="mdi:package" /> Produto solicitado</li>
                  <li><Icon icon="mdi:chart-line" /> Histórico de vendas</li>
                </ul>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.stepCard}>
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>3. Edite e Aprove</span>
                <p>
                  Na seção <strong>"Editar Afiliação"</strong> você pode:
                </p>
                <ul className={styles.featureList}>
                  <li><Icon icon="mdi:percent" /> Ajustar a comissão</li>
                  <li><Icon icon="mdi:check-circle" /> Alterar status para "Aprovado"</li>
                  <li><Icon icon="mdi:close-circle" /> Ou rejeitar se necessário</li>
                </ul>
                <p className="mb-0">
                  Clique em <strong>"Salvar Alterações"</strong> para confirmar.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.stepCard}>
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>4. Confirmação</span>
                <p>
                  Um modal de sucesso confirmará a atualização. O afiliado será notificado
                  e poderá começar a promover o produto.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Editar Comissão */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Editar Comissão</span>
          <p className={styles.sectionSubtitle}>
            Como ajustar a comissão de um afiliado em um produto
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:information" className={styles.infoIcon} />
          <div>
            <span className={styles.infoTitle}>Quando Editar Comissão</span>
            <p>
              Você pode editar a comissão de <strong>qualquer afiliado</strong>, incluindo
              afiliados de gerentes. Isso permite ajustar estratégias de comissionamento
              conforme necessário.
            </p>
          </div>
        </div>

        <Row className="g-4 mt-3">
          <Col lg={6}>
            <div className={styles.stepCard}>
              <Icon icon="mdi:account-search" className={styles.stepIcon} />
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>1. Encontre o Afiliado</span>
                <p>
                  Em <strong>Afiliados → Meus Afiliados</strong>, use os filtros ou busca
                  para encontrar o afiliado desejado.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.stepCard}>
              <Icon icon="mdi:eye" className={styles.stepIcon} />
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>2. Abra os Detalhes</span>
                <p>
                  Clique em <strong>"Ver detalhes"</strong> para abrir o drawer com todas
                  as informações do afiliado.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.stepCard}>
              <Icon icon="mdi:percent" className={styles.stepIcon} />
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>3. Edite a Comissão</span>
                <p>
                  Na seção <strong>"Editar Afiliação"</strong>, altere o valor da comissão
                  (em percentual) e/ou o status.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.stepCard}>
              <Icon icon="mdi:content-save" className={styles.stepIcon} />
              <div className={styles.stepContent}>
                <span className={styles.stepTitle}>4. Salve as Alterações</span>
                <p>
                  Clique em <strong>"Salvar Alterações"</strong>. A nova comissão será
                  aplicada a todas as vendas futuras.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Diferenças Importantes */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Diferenças Importantes</span>
          <p className={styles.sectionSubtitle}>
            Entenda as regras de cada tipo de usuário
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.comparisonCard}>
              <div className={styles.comparisonHeader}>
                <Icon icon="mdi:account-cash" size={32} />
                <span className={styles.comparisonTitle}>Vendedor</span>
              </div>
              <ul className={styles.featureList}>
                <li><Icon icon="mdi:plus-circle" /> Você adiciona produtos usando "Atribuir Afiliação"</li>
                <li><Icon icon="mdi:clock" /> Produtos adicionados ficam "pendentes" (precisam aprovação)</li>
                <li><Icon icon="mdi:store-off" /> NÃO pode acessar a loja pública</li>
                <li><Icon icon="mdi:eye-off" /> Vê apenas seus próprios dados</li>
                <li><Icon icon="mdi:shopping" /> Menu: Produtos, Vendas, Financeiro, Ajuda</li>
              </ul>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.comparisonCard}>
              <div className={styles.comparisonHeader}>
                <Icon icon="mdi:account-network" size={32} />
                <span className={styles.comparisonTitle}>Afiliado Direto</span>
              </div>
              <ul className={styles.featureList}>
                <li><Icon icon="mdi:check-circle" /> Você escolhe os produtos no cadastro</li>
                <li><Icon icon="mdi:check-all" /> Produtos aprovados automaticamente</li>
                <li><Icon icon="mdi:store-off" /> NÃO pode acessar a loja pública</li>
                <li><Icon icon="mdi:eye-off" /> Vê apenas seus próprios dados</li>
                <li><Icon icon="mdi:plus-circle" /> Você pode adicionar mais produtos depois</li>
              </ul>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.comparisonCard}>
              <div className={styles.comparisonHeader}>
                <Icon icon="mdi:account-plus" size={32} />
                <span className={styles.comparisonTitle}>Afiliado Indireto</span>
              </div>
              <ul className={styles.featureList}>
                <li><Icon icon="mdi:check-circle" /> Se cadastra sozinho na loja</li>
                <li><Icon icon="mdi:hand-pointing-right" /> Solicita afiliação aos produtos</li>
                <li><Icon icon="mdi:clock-outline" /> Status inicial: "pendente" (aguarda aprovação)</li>
                <li><Icon icon="mdi:store" /> PODE acessar a loja e outros produtos</li>
                <li><Icon icon="mdi:account-cog" /> Você edita comissão e status</li>
              </ul>
            </div>
          </Col>
        </Row>
      </section>

      {/* FAQ */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Perguntas Frequentes</span>
          <p className={styles.sectionSubtitle}>
            Dúvidas comuns sobre afiliados e vendedores
          </p>
        </div>

        <div className={styles.faqContainer}>
          <div className={styles.faqItem}>
            <div className={styles.faqQuestion}>
              <Icon icon="mdi:help-circle" />
              <span>Posso remover um produto de um vendedor ou afiliado direto?</span>
            </div>
            <div className={styles.faqAnswer}>
              <p>
                <strong>Sim.</strong> Para remover a afiliação de um produto, você precisa ir em
                <strong> Meus Afiliados</strong>, encontrar o afiliado e alterar o status para
                <strong> "Rejeitado"</strong>. Use apenas quando realmente necessário desativar a afiliação.
              </p>
            </div>
          </div>

          <div className={styles.faqItem}>
            <div className={styles.faqQuestion}>
              <Icon icon="mdi:help-circle" />
              <span>O que acontece quando crio um novo produto?</span>
            </div>
            <div className={styles.faqAnswer}>
              <p>
                <strong>Vendedores e Afiliados Diretos:</strong> O novo produto <strong>NÃO é vinculado automaticamente</strong>.
                Você precisa usar o botão <strong>"Atribuir Afiliação"</strong> em "Meus Afiliados" se quiser
                adicionar o produto para algum vendedor ou afiliado direto.
              </p>
              <p className="mb-0">
                Isso dá mais controle ao produtor sobre quais produtos cada vendedor/afiliado pode promover.
              </p>
            </div>
          </div>

          <div className={styles.faqItem}>
            <div className={styles.faqQuestion}>
              <Icon icon="mdi:help-circle" />
              <span>Posso editar a comissão de um afiliado de um gerente?</span>
            </div>
            <div className={styles.faqAnswer}>
              <p>
                <strong>Sim!</strong> Você pode editar a comissão de qualquer afiliado,
                mesmo que ele tenha sido cadastrado por um gerente. Apenas os dados pessoais
                do afiliado não podem ser editados (apenas o gerente pode fazer isso).
              </p>
            </div>
          </div>

          <div className={styles.faqItem}>
            <div className={styles.faqQuestion}>
              <Icon icon="mdi:help-circle" />
              <span>Um afiliado direto pode ver produtos de outros produtores?</span>
            </div>
            <div className={styles.faqAnswer}>
              <p>
                <strong>Não.</strong> Afiliados diretos e vendedores não podem acessar a loja
                pública. Eles só veem os produtos que você vinculou a eles e apenas quando
                o status está "aprovado".
              </p>
            </div>
          </div>

          <div className={styles.faqItem}>
            <div className={styles.faqQuestion}>
              <Icon icon="mdi:help-circle" />
              <span>Como identificar quem é vendedor, afiliado direto ou indireto?</span>
            </div>
            <div className={styles.faqAnswer}>
              <p>
                Use o <strong>filtro por tipo</strong> na tela "Meus Afiliados" ou observe os
                <strong> badges coloridos</strong> na tabela:
              </p>
              <ul className={styles.featureList}>
                <li>🟦 <strong>Azul</strong> = Vendedor</li>
                <li>🟩 <strong>Verde</strong> = Afiliado Direto</li>
                <li>🟨 <strong>Amarelo</strong> = Afiliado Indireto</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AfiliadosWiki;

